import { useState } from 'react'
import { Card, CardHeader } from '@/design-system/components/Card'
import { Button } from '@/design-system/components/Button'
import { Badge } from '@/design-system/components/Badge'
import { useMissions } from './hooks/useAdmin'
import type { AdminMission, MissionFilters } from './hooks/useAdmin'

const STATUS_LABELS: Record<AdminMission['status'], string> = {
  active: 'Aktiv',
  completed: 'Slutfört',
  cancelled: 'Avbruten',
  draft: 'Utkast',
}

const STATUS_VARIANTS: Record<AdminMission['status'], 'active' | 'complete' | 'urgent' | 'default'> = {
  active: 'active',
  completed: 'complete',
  cancelled: 'urgent',
  draft: 'default',
}

export function MissionManagement() {
  const [statusFilter, setStatusFilter] = useState<MissionFilters['status']>('all')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const { data, isLoading, cancelMission, completeMission } = useMissions({ status: statusFilter })

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (selected.size === data.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(data.map(m => m.id)))
    }
  }

  const cancelSelected = async () => {
    for (const id of selected) {
      await cancelMission(id)
    }
    setSelected(new Set())
  }

  const formatDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('sv-SE', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

  const getDeadlineClass = (deadline: string | null) => {
    if (!deadline) return ''
    const diff = new Date(deadline).getTime() - Date.now()
    if (diff < 0) return 'text-brand-danger'
    if (diff < 3 * 24 * 60 * 60 * 1000) return 'text-brand-warning'
    return 'text-text-secondary'
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Uppdrag</h1>
          <p className="text-sm text-text-secondary mt-1">{data.length} uppdrag</p>
        </div>
        {selected.size > 0 && (
          <Button variant="danger" size="sm" onClick={cancelSelected}>
            Avbryt {selected.size} valda
          </Button>
        )}
      </div>

      <Card>
        {/* Filters */}
        <CardHeader className="flex flex-wrap gap-2">
          {(['all', 'active', 'draft', 'completed', 'cancelled'] as const).map(s => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setSelected(new Set()) }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                statusFilter === s
                  ? 'bg-brand-accent text-white'
                  : 'bg-surface-overlay text-text-secondary hover:text-text-primary border border-surface-border'
              }`}
            >
              {s === 'all' ? 'Alla' : STATUS_LABELS[s as AdminMission['status']]}
            </button>
          ))}
        </CardHeader>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-border">
                <th className="px-5 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={selected.size === data.length && data.length > 0}
                    onChange={toggleAll}
                    className="rounded border-surface-border bg-surface-overlay accent-brand-accent"
                  />
                </th>
                {['ID', 'Titel', 'Klient', 'Status', 'Bilder', 'Deadline', ''].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <td key={j} className="px-5 py-3.5">
                        <div className="h-4 bg-surface-overlay rounded animate-pulse w-20" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-8 text-center text-text-muted text-sm">
                    Inga uppdrag hittades
                  </td>
                </tr>
              ) : (
                data.map(m => (
                  <tr
                    key={m.id}
                    className={`hover:bg-surface-overlay/50 transition-colors ${selected.has(m.id) ? 'bg-brand-accent/5' : ''}`}
                  >
                    <td className="px-5 py-3.5">
                      <input
                        type="checkbox"
                        checked={selected.has(m.id)}
                        onChange={() => toggleSelect(m.id)}
                        className="rounded border-surface-border bg-surface-overlay accent-brand-accent"
                      />
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="font-mono text-xs text-text-muted">{m.id}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="font-medium text-text-primary">{m.title}</p>
                    </td>
                    <td className="px-5 py-3.5 text-text-secondary">{m.client_name}</td>
                    <td className="px-5 py-3.5">
                      <Badge variant={STATUS_VARIANTS[m.status]}>{STATUS_LABELS[m.status]}</Badge>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1">
                        <span className="tabular-nums font-medium text-text-primary">{m.images_delivered}</span>
                        <span className="text-text-muted">/</span>
                        <span className="tabular-nums text-text-muted">{m.required_photos}</span>
                      </div>
                      {/* Progress bar */}
                      <div className="mt-1 h-1 w-20 bg-surface-overlay rounded-full overflow-hidden">
                        <div
                          className="h-full bg-brand-accent rounded-full transition-all"
                          style={{ width: `${Math.min(100, (m.images_delivered / m.required_photos) * 100)}%` }}
                        />
                      </div>
                    </td>
                    <td className={`px-5 py-3.5 whitespace-nowrap text-sm ${getDeadlineClass(m.deadline)}`}>
                      {formatDate(m.deadline)}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2 justify-end">
                        <Button variant="ghost" size="sm">Detaljer</Button>
                        {m.status === 'active' && (
                          <>
                            <Button variant="secondary" size="sm" onClick={() => completeMission(m.id)}>
                              ✓ Slutför
                            </Button>
                            <Button variant="danger" size="sm" onClick={() => cancelMission(m.id)}>
                              Avbryt
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
